import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  FlaskConical,
  Snowflake,
  Wind,
  FileCheck,
  ShieldCheck,
  QrCode,
  ScanLine,
} from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const PEPDIRECT_URL = "https://pepdirect.co";
const PEPDIRECT_COA_URL = "https://pepdirect.co/coa";
const PEPDIRECT_VERIFY_URL = "https://pepdirect.co/verify";

const EXCLUSIVITY_LINE =
  "PepDirect is the sole authorized distributor of Trenta Medical compounds. We do not sell direct. All retail and volume orders are fulfilled exclusively through PepDirect.";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut", delay },
});

export default function Home() {
  const location = useLocation();

  usePageMeta({
    title: "Trenta Medical · Research Compound Manufacturer",
    description:
      "Trenta Medical is a Netherlands-based research compound manufacturer supplying the UK and EU. In-house synthesis, nitrogen-sealed fill-finish, and independent quality control on every lot.",
    canonical: "https://trentamedical.com/",
  });

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }, [location.hash]);

  return (
    <div style={{ background: "#1A2B5E" }}>
      {/* ── HERO (navy) ──────────────────────────────────────── */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="container mx-auto px-5 lg:px-8 pt-10 pb-8 sm:pt-16 sm:pb-14 lg:pt-24 lg:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <p className="label-mono mb-3 sm:mb-4" style={{ color: "#8FA8E8" }}>
              § Netherlands · Supplying UK &amp; EU
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mb-3 sm:mb-5 text-white">
              Research Compound
              <br />
              Manufacturer.
            </h1>
            <p
              className="text-sm sm:text-base lg:text-lg leading-relaxed mb-5 sm:mb-7 max-w-2xl"
              style={{ color: "rgba(255,255,255,0.7)" }}
            >
              In-house synthesis, nitrogen-sealed fill-finish, and independent
              quality control on every lot.
            </p>
            <a
              href={PEPDIRECT_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-white inline-flex items-center justify-center gap-2 px-7 py-3 sm:py-3.5 text-sm font-bold tracking-wide"
              data-testid="hero-pepdirect-cta"
            >
              Order via PepDirect
              <ArrowUpRight size={15} />
            </a>
          </motion.div>

          {/* Verify card — white accent, visible without scrolling at 390px */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 sm:mt-9 max-w-xl p-4 sm:p-6 flex items-start gap-3.5 sm:gap-5"
            style={{ border: "1.5px solid #8FA8E8", background: "#FFFFFF" }}
            data-testid="hero-verify-card"
          >
            <div
              className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center"
              style={{ background: "#E3EBFC", border: "1px solid #C7D6F8" }}
              aria-hidden="true"
            >
              <QrCode size={20} style={{ color: "#2D5BE3" }} />
            </div>
            <div>
              <p
                className="text-sm sm:text-base font-bold mb-1"
                style={{ color: "#0F0F0F" }}
              >
                Scanned the code on your box?
              </p>
              <p
                className="text-xs sm:text-sm leading-relaxed mb-3"
                style={{ color: "#4A4F5E" }}
              >
                Verify your product&apos;s authenticity and view its lot
                certificate.
              </p>
              <a
                href={PEPDIRECT_VERIFY_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-navy inline-flex items-center gap-1.5 px-5 py-2 text-xs sm:text-sm font-semibold"
                data-testid="hero-verify-btn"
              >
                Verify your product
                <ArrowUpRight size={13} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── EXCLUSIVE DISTRIBUTOR BANNER (white — loudest) ───── */}
      <section
        className="py-9 sm:py-12"
        style={{ background: "#FFFFFF" }}
        data-testid="distributor-banner"
      >
        <div className="container mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <p className="label-mono mb-2.5">§ Exclusive Distribution</p>
              <p
                className="text-lg sm:text-xl lg:text-2xl font-bold leading-snug max-w-3xl"
                style={{ color: "#0F0F0F" }}
              >
                {EXCLUSIVITY_LINE}
              </p>
            </div>
            <a
              href={PEPDIRECT_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-navy flex-shrink-0 inline-flex items-center gap-2 px-7 py-3 text-sm font-bold tracking-wide"
              data-testid="distributor-banner-btn"
            >
              Visit pepdirect.co
              <ArrowUpRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* ── WHAT WE DO (navy) ────────────────────────────────── */}
      <section
        id="what-we-do"
        className="py-16 sm:py-20 lg:py-28 scroll-mt-20"
        data-testid="what-we-do-section"
      >
        <div className="container mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp()} className="mb-10 sm:mb-14 max-w-2xl">
            <p className="label-mono mb-3" style={{ color: "#8FA8E8" }}>
              § What We Do
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-5 leading-tight text-white">
              Manufacturing, From Synthesis to Dispatch
            </h2>
            <p
              className="text-sm sm:text-base leading-relaxed"
              style={{ color: "rgba(255,255,255,0.7)" }}
            >
              Trenta Medical is a Netherlands-based manufacturer of research
              compounds — including metabolic and tissue-recovery research
              peptides — supplying laboratories across the UK and EU. Every
              stage of production is handled under controlled conditions, from
              raw synthesis through to final sealed dispatch.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                icon: FlaskConical,
                title: "Synthesis Capability",
                desc: "In-house peptide synthesis with controlled purification, ensuring lot-to-lot consistency across all compound classes.",
              },
              {
                icon: Wind,
                title: "Nitrogen-Sealed Fill-Finish",
                desc: "Vials are filled and sealed under nitrogen atmosphere, protecting compound stability from oxidation and moisture.",
              },
              {
                icon: Snowflake,
                title: "Cold-Chain Dispatch",
                desc: "Temperature-controlled handling and dispatch protocols preserve compound integrity from facility to laboratory.",
              },
            ].map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                {...fadeUp(i * 0.08)}
                className="card-navy p-6 sm:p-8"
                data-testid={`what-we-do-card-${i}`}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center mb-5"
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                >
                  <Icon size={18} style={{ color: "#8FA8E8" }} />
                </div>
                <h3 className="text-base sm:text-lg font-bold mb-2.5 text-white">
                  {title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.6)" }}
                >
                  {desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUALITY & TESTING (white accent) ─────────────────── */}
      <section
        id="quality"
        className="py-16 sm:py-20 lg:py-28 scroll-mt-20"
        style={{ background: "#FFFFFF" }}
        data-testid="quality-section"
      >
        <div className="container mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <motion.div {...fadeUp()}>
              <p className="label-mono mb-3">§ Quality &amp; Testing</p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-5 leading-tight"
                style={{ color: "#0F0F0F" }}
              >
                Every Lot Independently Tested by Janoshik
              </h2>
              <p
                className="text-sm sm:text-base leading-relaxed mb-6"
                style={{ color: "#4A4F5E" }}
              >
                Every production lot is independently tested by Janoshik
                Analytical — HPLC purity and pH — before release. Certificates
                are published ahead of purchase, so results can be reviewed
                before any order is placed.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  {
                    icon: FlaskConical,
                    title: "HPLC Purity Analysis",
                    desc: "Independent chromatographic verification of identity and purity on every lot",
                  },
                  {
                    icon: FileCheck,
                    title: "Certificates Published Pre-Purchase",
                    desc: "Full lot certificates available for review before any purchase decision",
                  },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-4">
                    <div
                      className="flex-shrink-0 w-10 h-10 flex items-center justify-center"
                      style={{ background: "#E3EBFC", border: "1px solid #C7D6F8" }}
                    >
                      <Icon size={16} style={{ color: "#2D5BE3" }} />
                    </div>
                    <div>
                      <p
                        className="text-sm font-bold mb-0.5"
                        style={{ color: "#0F0F0F" }}
                      >
                        {title}
                      </p>
                      <p className="text-sm" style={{ color: "#4A4F5E" }}>
                        {desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href={PEPDIRECT_COA_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold transition-colors duration-200 border-b pb-0.5 hover:text-[#2D5BE3]"
                style={{ color: "#1A2B5E", borderColor: "rgba(26,43,94,0.4)" }}
                data-testid="coa-library-link"
              >
                View certificates at PepDirect&apos;s COA library
                <ArrowUpRight size={13} />
              </a>
            </motion.div>
            <motion.div {...fadeUp(0.1)}>
              <img
                src="/lab-testing.png"
                alt="Independent laboratory testing"
                className="w-full h-[280px] sm:h-[380px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── AUTHENTICITY (navy) ──────────────────────────────── */}
      <section
        id="authenticity"
        className="py-16 sm:py-20 lg:py-28 scroll-mt-20"
        data-testid="authenticity-section"
      >
        <div className="container mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <motion.div {...fadeUp()}>
              <p className="label-mono mb-3" style={{ color: "#8FA8E8" }}>
                § Authenticity
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-5 leading-tight text-white">
                Every Unit, Verifiable
              </h2>
              <p
                className="text-sm sm:text-base leading-relaxed mb-8"
                style={{ color: "rgba(255,255,255,0.7)" }}
              >
                Every unit that leaves our facility carries a lot number, a void
                seal, and a unique verification code. Authenticity can be
                confirmed in seconds — and each code links back to the lot&apos;s
                published certificate.
              </p>
              <div className="flex flex-wrap items-center gap-4 mb-8">
                {[
                  { icon: ScanLine, label: "Lot number" },
                  { icon: ShieldCheck, label: "Void seal" },
                  { icon: QrCode, label: "Unique verification code" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2.5 px-4 py-2.5"
                    style={{
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "#12204A",
                    }}
                  >
                    <Icon size={15} style={{ color: "#8FA8E8" }} />
                    <span className="text-sm font-semibold text-white">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
              <a
                href={PEPDIRECT_VERIFY_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-white inline-flex items-center gap-2 px-7 py-3 text-sm font-bold tracking-wide"
                data-testid="authenticity-verify-btn"
              >
                Verify a product
                <ArrowUpRight size={15} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
